import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withFormik } from 'formik';
import isNil from 'lodash/isNil';
// components
import Modal from 'shared/modals/Modal';
import LoadingModal from 'shared/loading/LoadingModal';
import UnsavedChanges from 'shared/modals/UnsavedChanges';
import { FormikInput, FormikSelect } from 'shared/forms';
import MappingOrderProperties from './controls/MappingOrderProperties';
import SpreadsheetPreviewer from '../../formControls/SpreadsheetPreviewer';
// helpers
import toastr from 'shared/helpers/toastr';
import errorToastr from 'shared/helpers/errorToastr';
// constants
import { FIXED_VALUE_INDICATOR } from './constants';
// fetch
import { getPendingOrderProperties, getPendingOrderPresetDetails, updatePendingOrderPreset, insertPendingOrderPreset } from 'src/fetch/pendingOrders';
import { hitGetEndpoint } from 'src/fetch/general';
import { getOrganizationsLookup as fetchOrganizations } from 'src/fetch/organizations';
// redux
import { getOrganizationsIncludingDeleted, setOrganizations } from 'src/slices/organizations';
// schema
import MappingPresetSchema from './MappingPresetSchema';

const MappingPresetModal = ({
  values,
  previewingSheet,
  resetForm,
  touched,
  errors,
  dirty,
  isSubmitting,
  setFieldValue,
  setFieldTouched,
  handleChange,
  handleBlur,
  handleSubmit,
  close
}) => {
  const dispatch = useDispatch();
  const organizations = useSelector(getOrganizationsIncludingDeleted);

  const [isLoading, setIsLoading] = useState(false);
  const [orderProperties, setOrderProperties] = useState([]);

  const [sheetColumns, setSheetColumns] = useState([]);
  const [sheetCells, setSheetCells] = useState([]);

  const getOrderMappingRulesFieldName = (orderProperties, name, id) => {
    const rule = orderProperties?.find(v => v.id === id);
    const idx = orderProperties?.indexOf(rule);

    return `orderMappingRules.${idx}.${name}`;
  };

  const closeModal = () => {
    resetForm();
    close(false);
  };

  const load = async () => {
    setIsLoading(true);
    try {
      const propertiesResponse = await getPendingOrderProperties();

      if (propertiesResponse && propertiesResponse.length) {
        await Promise.all(
          propertiesResponse.map(val => {
            const { id, endpoint } = val;

            setFieldValue(getOrderMappingRulesFieldName(propertiesResponse, 'mappingPropertyId', id), id);

            if (endpoint && endpoint.length) {
              return hitGetEndpoint({ endpoint })().then(data => {
                val.fixedValues = data;
              });
            } else {
              return Promise.resolve();
            }
          })
        );
      }

      setOrderProperties(propertiesResponse);

      if (values && values.id) {
        const presetResponse = await getPendingOrderPresetDetails(values.id);

        if (!previewingSheet?.headers) {
          setSheetColumns(presetResponse?.orderMappingRules?.map(({ columnName }) => columnName)?.filter(c => c && c.length) ?? []);
        }

        mapFixedValues(presetResponse?.orderMappingRules);

        presetResponse.orderMappingRules = propertiesResponse?.map(({ id: mappingPropertyId }) => ({
          mappingPropertyId,
          ...(presetResponse.orderMappingRules?.find(r => r.mappingPropertyId === mappingPropertyId) ?? {})
        }));

        resetForm(presetResponse);
      }
    } catch (error) {
      console.error(error);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (!organizations || !organizations.length) {
      fetchOrganizations().then(data => dispatch(setOrganizations({ organizations: data })));
    }
    load();
  }, []);

  useEffect(() => {
    resetForm();
    setSheetColumns(previewingSheet?.headers || []);
    setSheetCells(previewingSheet?.rows || []);
  }, [previewingSheet]);

  return (
    <Modal isOpen handleClose={closeModal} containerStyle='large'>
      <div className='modal-header'>
        <h5 className='modal-title' id='mapping-preset-modal'>
          {values && values.id ? 'Edit' : 'Add'} Spreadsheet Mapping Preset
        </h5>
        <button className='button large close' onClick={closeModal} />
      </div>
      <form onSubmit={handleSubmit} noValidate>
        <section className='modal-body mapping-preset'>
          <UnsavedChanges dirty={dirty && !isSubmitting} />
          {/* <LoadingModal isOpen={isLoading} text='Loading Preset' />
          <LoadingModal isOpen={isSubmitting} text='Saving Preset' /> */}
          <div className='formik-row'>
            <fieldset className='formik-section' style={{ marginTop: 0 }}>
              <div className='formik-row'>
                <FormikInput
                  wrapperClass='wrapper-two-column'
                  type='text'
                  label='Preset Name'
                  name='name'
                  required
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
                <FormikSelect
                  wrapperClass='wrapper-two-column'
                  label='Organization'
                  name='organizationId'
                  required
                  value={values.organizationId}
                  onChange={selected => setFieldValue('organizationId', selected.value)}
                  options={organizations?.map(({ id, name }) => ({ label: name, value: id }))}
                  onBlur={setFieldTouched}
                  touched={touched}
                  errors={errors}
                />
              </div>

              {sheetCells?.length ? (
                <div className='formik-row'>
                  <SpreadsheetPreviewer previewingSheet={previewingSheet} />
                </div>
              ) : null}

              <div className='formik-row'>
                <div className='wrapper-three-column'>
                  <label>Field Name</label>
                </div>
                <div className='wrapper-three-column'>
                  <label>Map to Column</label>
                </div>
                <div className='wrapper-three-column'>
                  <label>Preset Value</label>
                </div>
              </div>

              {orderProperties && orderProperties.length && (
                <MappingOrderProperties
                  values={values}
                  touched={touched}
                  errors={errors}
                  setFieldValueForMappings={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  orderProperties={orderProperties}
                  sheetColumns={sheetColumns}
                />
              )}
            </fieldset>
          </div>
        </section>
        <section className='modal-footer toolbar toolbar--xxs'>
          <div className='toolbar--column toolbar--right'>
            <button type='submit' disabled={isSubmitting} className={`button large primary text-align--center toolbar--button--margin`}>
              Save
            </button>
            <button
              type='button'
              disabled={isSubmitting}
              className={`button large white text-align--center toolbar--button--margin`}
              onClick={closeModal}
            >
              Cancel
            </button>
          </div>
        </section>
      </form>
    </Modal>
  );
};

const mapFixedValues = rules => {
  rules.forEach(rule => {
    if (!rule.columnName && rule.fixedValue) {
      rule.columnName = FIXED_VALUE_INDICATOR;
    }
  });
};

const unmapFixedValues = rules => {
  rules.forEach(val => {
    if (val?.columnName === FIXED_VALUE_INDICATOR) {
      val.columnName = null;
    }
  });
};

export default withFormik({
  enableReinitialize: true,
  mapPropsToValues: ({ id }) => {
    return {
      id,
      name: '',
      organizationId: '',
      orderMappingRules: []
    };
  },
  validationSchema: MappingPresetSchema,
  handleSubmit: async (values, { setSubmitting, resetForm, props: { close } }) => {
    setSubmitting(true);

    // create payload for backend
    // copy the values to avoid weird errors from Formik when transforming
    const rules = values.orderMappingRules?.map(r => ({ ...r })) ?? [];
    const payload = { ...values, orderMappingRules: rules };
    unmapFixedValues(payload.orderMappingRules);

    // removing order mappings with no columns or fixed values
    payload.orderMappingRules = payload.orderMappingRules?.filter(r => !isNil(r?.columnName) || !isNil(r?.fixedValue));

    try {
      var result;
      if (payload.id) {
        result = await updatePendingOrderPreset(payload.id, payload);
      } else {
        result = await insertPendingOrderPreset(payload);
      }

      mapFixedValues(result.orderMappingRules);

      resetForm(result);
      toastr('success', 'Preset Saved Successfully.');
      close(true);
    } catch (error) {
      errorToastr({
        error,
        messagePrefix: 'Error saving Preset.'
      });
    }

    setSubmitting(false);
  }
})(withRouter(MappingPresetModal));
